import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useSeo } from "@/hooks/useSeo";
import { BookOpen, Briefcase, ShieldCheck } from "lucide-react";

const SUBJECTS = ["Math", "English", "Science", "Social Studies", "French",
                    "Physics", "Chemistry", "Biology", "Coding", "Reading"];
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const GRADES = ["K-3", "4-6", "7-9", "10-12", "Post-secondary"];
const CURRICULA = ["Alberta", "Ontario", "BC", "Quebec", "Manitoba", "Saskatchewan",
                     "Nova Scotia", "New Brunswick", "Newfoundland", "PEI", "Yukon", "Other"];
const MODES = ["physical", "online", "both"];

const chipList = (v, setV, options, testidPrefix) => (
  <div className="flex flex-wrap gap-2">
    {options.map((o) => {
      const on = v.includes(o);
      return (
        <button type="button" key={o}
          data-testid={`${testidPrefix}-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          onClick={() => setV(on ? v.filter((x) => x !== o) : [...v, o])}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
            on
              ? "bg-[#064E3B] text-white border-[#064E3B]"
              : "bg-white text-[#111827] border-[#E5E7EB] hover:border-[#064E3B]"
          }`}
        >
          {o}
        </button>
      );
    })}
  </div>
);

export default function CentreTeacherApply() {
  useSeo({
    title: "Teach at Teacher4Me Learning Centre — 30/70 revenue split | Teacher4Me.ca",
    description:
      "Apply to teach at Teacher4Me Learning Centre. 30% of tuition to teachers, 70% to the centre. In-person or online. Flexible hours.",
    canonical: "https://teacher4me.ca/centre/teacher-apply",
  });

  const { user } = useAuth();
  const [step, setStep] = useState(1);

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [pw, setPw] = useState("");
  const [highestEdu, setHighestEdu] = useState("");
  const [yearsExp, setYearsExp] = useState(0);
  const [subjects, setSubjects] = useState([]);
  const [gradeLevels, setGradeLevels] = useState([]);
  const [curriculum, setCurriculum] = useState([]);
  const [curriculumDesc, setCurriculumDesc] = useState("");
  const [days, setDays] = useState([]);
  const [hours, setHours] = useState("");
  const [mode, setMode] = useState("physical");

  const [resumeUrl, setResumeUrl] = useState("");
  const [govIdUrl, setGovIdUrl] = useState("");
  const [eduUrl, setEduUrl] = useState("");
  const [certUrl, setCertUrl] = useState("");
  const [references, setReferences] = useState("");

  const [agreementBody, setAgreementBody] = useState("");
  const [agreementRead, setAgreementRead] = useState(false);
  const [signature, setSignature] = useState("");
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    api.get("/api/centre/agreements/teaching_partner/latest")
      .then(({ data }) => setAgreementBody(data.body))
      .catch(() => setAgreementBody("Failed to load agreement text."));
  }, []);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!agreementRead) return toast.error("Please read the Teaching Partner Agreement");
    if (!signature.trim()) return toast.error("Please type your full legal name as signature");
    if (signature.trim().toLowerCase() !== fullName.trim().toLowerCase())
      return toast.error("Signature must exactly match your full legal name");
    setBusy(true);
    try {
      const docs = { resume_url: resumeUrl, gov_id_url: govIdUrl,
                     edu_url: eduUrl, cert_url: certUrl, references };
      const { data } = await api.post("/api/centre/teacher-apply", {
        full_name: fullName, email, phone, password: pw,
        highest_edu: highestEdu, years_exp: Number(yearsExp) || 0,
        subjects, grade_levels: gradeLevels,
        curriculum_knowledge: curriculum, curriculum_desc: curriculumDesc,
        availability_days: days, preferred_hours: hours, mode,
        docs,
        agreement_signature: signature.trim(), agreement_version: "v1",
      });
      localStorage.setItem("tmo_token", data.access_token);
      localStorage.setItem("tmo_user", JSON.stringify(data.user));
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Application failed");
    } finally {
      setBusy(false);
    }
  };

  if (submitted || (user && user.role === "teacher")) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16">
        <div className="bg-[#ECFDF5] border border-[#A7F3D0] rounded-2xl p-8" data-testid="teacher-submitted">
          <h1 className="font-display font-black text-2xl">Application received</h1>
          <p className="text-sm text-[#065F46] mt-3">
            Thanks for applying to Teacher4Me Learning Centre. Our team will review
            your documents and get back to you within <b>3 business days</b>. You'll
            receive an email at <b>{email || "the address on file"}</b> once your
            status changes.
          </p>
          <p className="text-xs text-[#4B5563] mt-4">
            Your signed Teaching Partner Agreement (v1) is on file. You can request a
            copy any time from your account.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-10">
      <div className="mb-8">
        <div className="text-xs font-semibold text-[#064E3B] uppercase tracking-wider flex items-center gap-2">
          <Briefcase className="w-4 h-4" /> Independent teaching partner · 30/70 split
        </div>
        <h1 className="font-display font-black text-3xl sm:text-4xl mt-2">
          Teach at Teacher4Me Learning Centre
        </h1>
        <p className="text-sm text-[#4B5563] mt-3 max-w-2xl">
          Bring your teaching experience to Teacher4Me. In-person (Calgary NW) or online.
          Independent-contractor model: <b>30% of tuition</b> goes to the teacher,
          70% covers facilities, marketing, admin, and technology.
        </p>
      </div>

      <div className="flex gap-2 mb-6 text-xs" data-testid="teacher-apply-stepper">
        {["Basics", "Experience", "Availability", "Documents", "Sign"].map((label, i) => (
          <div key={label} className={`px-3 py-1 rounded-full border ${
            step === i + 1 ? "bg-[#D92D20] text-white border-[#D92D20]" : "bg-white text-[#4B5563] border-[#E5E7EB]"
          }`}>
            {i + 1}. {label}
          </div>
        ))}
      </div>

      <form onSubmit={submit} className="space-y-6" data-testid="teacher-apply-form">
        {step === 1 && (
          <Card title="Basics" icon={ShieldCheck}>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Full legal name*" value={fullName} setValue={setFullName} testid="t-name" required />
              <Field label="Email*" type="email" value={email} setValue={setEmail} testid="t-email" required />
              <Field label="Phone" value={phone} setValue={setPhone} testid="t-phone" />
              <Field label="Password* (min 6)" type="password" value={pw} setValue={setPw} testid="t-pw" minLength={6} required />
            </div>
          </Card>
        )}

        {step === 2 && (
          <Card title="Experience" icon={BookOpen}>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Highest education (e.g. B.Ed, M.Ed, PhD)" value={highestEdu} setValue={setHighestEdu} testid="t-edu" />
              <Field label="Years of teaching / tutoring experience" type="number" value={yearsExp} setValue={setYearsExp} testid="t-yrs" />
            </div>
            <div className="mt-4">
              <div className="text-sm font-semibold mb-2">Subjects you can teach</div>
              {chipList(subjects, setSubjects, SUBJECTS, "t-sub")}
            </div>
            <div className="mt-4">
              <div className="text-sm font-semibold mb-2">Grade levels</div>
              {chipList(gradeLevels, setGradeLevels, GRADES, "t-grade")}
            </div>
            <div className="mt-4">
              <div className="text-sm font-semibold mb-2">Provincial curriculum knowledge</div>
              {chipList(curriculum, setCurriculum, CURRICULA, "t-curric")}
            </div>
            <div className="mt-4">
              <label className="text-sm font-semibold">Briefly describe your curriculum experience</label>
              <textarea data-testid="t-curric-desc" rows={3} value={curriculumDesc}
                onChange={(e) => setCurriculumDesc(e.target.value)}
                className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#D92D20]" />
            </div>
          </Card>
        )}

        {step === 3 && (
          <Card title="Availability" icon={BookOpen}>
            <div>
              <div className="text-sm font-semibold mb-2">Available days</div>
              {chipList(days, setDays, DAYS, "t-day")}
            </div>
            <div className="mt-4 grid md:grid-cols-2 gap-4">
              <Field label="Preferred hours (e.g. Weekdays 4-8pm)" value={hours} setValue={setHours} testid="t-hours" />
              <div>
                <label className="text-sm font-semibold">Preferred mode</label>
                <select data-testid="t-mode" value={mode} onChange={(e) => setMode(e.target.value)}
                  className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm bg-white">
                  {MODES.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            </div>
          </Card>
        )}

        {step === 4 && (
          <Card title="Documents (URLs or links to Google Drive / Dropbox)" icon={ShieldCheck}>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Resume URL" value={resumeUrl} setValue={setResumeUrl} testid="t-doc-resume" />
              <Field label="Government-issued ID URL" value={govIdUrl} setValue={setGovIdUrl} testid="t-doc-id" />
              <Field label="Education transcript / degree URL" value={eduUrl} setValue={setEduUrl} testid="t-doc-edu" />
              <Field label="Teaching certifications URL" value={certUrl} setValue={setCertUrl} testid="t-doc-cert" />
            </div>
            <div className="mt-4">
              <label className="text-sm font-semibold">References (names + phone / email)</label>
              <textarea data-testid="t-refs" rows={3} value={references}
                onChange={(e) => setReferences(e.target.value)}
                className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#D92D20]" />
            </div>
            <p className="text-xs text-[#4B5563] mt-2">
              You can leave URLs blank and email documents to <b>hello@teacher4me.ca</b> after applying.
            </p>
          </Card>
        )}

        {step === 5 && (
          <Card title="Teaching Partner Agreement" icon={ShieldCheck}>
            <pre data-testid="teacher-agreement-body"
              className="whitespace-pre-wrap text-xs leading-relaxed bg-[#F9FAFB] border border-[#E5E7EB]
                         rounded-md p-4 max-h-72 overflow-auto">
              {agreementBody}
            </pre>
            <label className="flex items-center gap-2 text-sm mt-4">
              <input data-testid="teacher-agreement-read" type="checkbox" checked={agreementRead}
                onChange={(e) => setAgreementRead(e.target.checked)}
                className="w-4 h-4" />
              I have read and agree to the Teaching Partner Agreement (v1).
            </label>
            <div className="mt-3">
              <label className="text-sm font-semibold">
                Type your full legal name as electronic signature*
              </label>
              <input data-testid="teacher-agreement-signature" required value={signature}
                onChange={(e) => setSignature(e.target.value)}
                className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#D92D20]"
                placeholder="Full legal name" />
              <p className="text-xs text-[#4B5563] mt-1">Must match your name in step 1.</p>
            </div>
          </Card>
        )}

        <div className="flex items-center justify-between pt-2">
          <button type="button" data-testid="teacher-back"
            onClick={() => setStep(Math.max(1, step - 1))}
            disabled={step === 1}
            className="text-sm font-semibold text-[#4B5563] disabled:opacity-40">
            ← Back
          </button>
          {step < 5 ? (
            <button type="button" data-testid="teacher-next"
              onClick={() => setStep(Math.min(5, step + 1))}
              className="btn-primary">Next →</button>
          ) : (
            <button type="submit" data-testid="teacher-apply-submit" disabled={busy} className="btn-primary">
              {busy ? "Submitting..." : "Submit Application"}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, setValue, testid, type = "text", required = false, minLength }) {
  return (
    <div>
      <label className="text-sm font-semibold">{label}</label>
      <input data-testid={testid} type={type} required={required} minLength={minLength}
        value={value} onChange={(e) => setValue(e.target.value)}
        className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#D92D20]" />
    </div>
  );
}

function Card({ title, icon: Icon, children }) {
  return (
    <section className="bg-white border border-[#E5E7EB] rounded-2xl p-6">
      <h2 className="font-display font-bold text-lg mb-4 flex items-center gap-2">
        <Icon className="w-5 h-5 text-[#D92D20]" /> {title}
      </h2>
      {children}
    </section>
  );
}
